import {
  Card,
  Text,
  Button,
  Popover,
  ActionList,
  BlockStack,
  InlineStack,
  DropZone,
  Spinner,
  SkeletonBodyText,
  SkeletonDisplayText,
} from "@medentic/polaris-react";
import { EditIcon, DeleteIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import { CoreAvatar } from "../../types";

interface ProfileImageProps {
  avatar?: CoreAvatar;
  loading?: boolean;
}

function ProfileImage({ avatar, loading = false }: ProfileImageProps) {
  const { t } = useTranslation();
  const [popoverActive, setPopoverActive] = useState(false);
  const [tempPreview, setTempPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { clinicId, patientId } = useParams<{
    patientId: string;
    clinicId: string;
  }>();
  const { POST, DELETE } = useApiRequest();
  const queryClient = useQueryClient();

  // Cleanup preview URL when component unmounts or when upload completes
  useEffect(() => {
    return () => {
      if (tempPreview) {
        URL.revokeObjectURL(tempPreview);
      }
    };
  }, [tempPreview]);

  const { mutate: uploadImageMutation } = useMutation({
    mutationFn: async (file: File) => {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("data", JSON.stringify({ id: "core_avatar" }));

      return POST(
        `/api/healthcare-companies/${clinicId}/patients/${patientId}/upload`,
        formData
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patientDetails", patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setPopoverActive(false);
      setTimeout(() => {
        setTempPreview(null);
        setIsUploading(false);
      }, 1000);
    },
    onError: (error) => {
      console.error("Error uploading image:", error);
      setIsUploading(false);
      setTempPreview(null);
    },
  });

  const { mutate: deleteImageMutation, isPending: isDeleting } = useMutation({
    mutationFn: async () => {
      return DELETE(
        `/api/healthcare-companies/${clinicId}/patients/${patientId}/upload`,
        false,
        { id: "core_avatar" }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patientDetails", patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setPopoverActive(false);
    },
    onError: (error) => {
      console.error("Error removing image:", error);
    },
  });

  const togglePopoverActive = useCallback(
    () => setPopoverActive((active) => !active),
    []
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setTempPreview(previewUrl);
      uploadImageMutation(file);
    }
  };

  const handleChangeImage = useCallback(() => {
    fileInputRef.current?.click();
    setPopoverActive(false);
  }, []);

  const handleRemoveImage = useCallback(() => {
    deleteImageMutation();
  }, [deleteImageMutation]);

  const handleDropZoneDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[]) => {
      if (acceptedFiles[0]) {
        const previewUrl = URL.createObjectURL(acceptedFiles[0]);
        setTempPreview(previewUrl);
        uploadImageMutation(acceptedFiles[0]);
      }
    },
    [uploadImageMutation]
  );

  const displayedImage = tempPreview || avatar?.value;
  const srcSet = avatar?.sources
    ?.map(({ src, width }) => `${src} ${width}w`)
    .join(", ");
  const sizes = "800px"; // Using the largest size since this is a profile image

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between">
          {loading ? (
            <SkeletonDisplayText size="medium" />
          ) : (
            <Text as="h2" variant="headingMd">
              {t("patientDetails.profilePicture")}
            </Text>
          )}
          {!loading && displayedImage && (
            <Popover
              active={popoverActive}
              activator={
                <Button
                  onClick={togglePopoverActive}
                  variant="plain"
                  disclosure
                >
                  {t("common.edit")}
                </Button>
              }
              onClose={togglePopoverActive}
              autofocusTarget="first-node"
            >
              <ActionList
                actionRole="menuitem"
                items={[
                  {
                    content: t("patientDetails.changeImage"),
                    icon: EditIcon,
                    onAction: handleChangeImage,
                  },
                  {
                    content: t("patientDetails.remove"),
                    icon: DeleteIcon,
                    onAction: handleRemoveImage,
                    disabled: isDeleting,
                    destructive: true,
                  },
                ]}
              />
            </Popover>
          )}
        </InlineStack>
        {loading ? (
          <div
            style={{
              width: "100%",
              aspectRatio: "16/9",
              background: "#f1f2f3",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SkeletonBodyText lines={1} />
          </div>
        ) : displayedImage ? (
          <div
            style={{
              width: "100%",
              aspectRatio: "16/9",
              background: "#f1f2f3",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <img
              src={displayedImage}
              srcSet={srcSet}
              sizes={sizes}
              alt={t("patientDetails.profilePicture")}
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "8px",
                maxWidth: "800px",
                maxHeight: "800px",
                objectFit: "contain",
              }}
            />
            {isUploading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                }}
              >
                <Spinner size="large" />
              </div>
            )}
          </div>
        ) : (
          <DropZone
            onDrop={handleDropZoneDrop}
            allowMultiple={false}
            type="image"
            accept="image/*"
          >
            <DropZone.FileUpload
              actionTitle={t("patientDetails.addPhoto")}
              actionHint={t("patientDetails.addPhotoHint")}
            />
          </DropZone>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          style={{ display: "none" }}
        />
      </BlockStack>
    </Card>
  );
}

export default ProfileImage;
